import { useLingui } from "@lingui/react/macro";
import { Button, Center, Group, Menu } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import { Link, LinkOptions, RegisteredRouter } from "@tanstack/react-router";

import { HeaderBase, HeaderUser } from "@/components/header";
import CreateBooking from "@/features/bookings/create-booking/create-booking-modal";
import { Can } from "@/shared/providers/casl-provider";
import { useAppAbility } from "@/shared/providers/casl-provider/use-app-ability";

type Routes = LinkOptions<RegisteredRouter>["to"];

export default function Header() {
  const { t } = useLingui();

  const ability = useAppAbility();

  const links: (
    | {
        link: Routes;
        label: string;
        nested?: (
          | {
              link: Routes;
              label: string;
            }
          | undefined
        )[];
      }
    | undefined
  )[] = [
    ability.can("read", "Booking")
      ? { link: "/admin/bookings", label: t`Bookinger` }
      : undefined,
    ability.can("read", "Product")
      ? {
          link: "/admin/products",
          label: t`Produkt`,
          nested: [
            { link: "/admin/products", label: t`Alle produkt` },
            ability.can("read", "ProductCategory")
              ? { link: "/admin/products/categories", label: t`Kategorier` }
              : undefined,
          ],
        }
      : undefined,
    ability.can("read", "Employee")
      ? { link: "/admin/employees", label: t`Personell` }
      : undefined,
    ability.can("read", "Customer")
      ? { link: "/admin/customers", label: t`Kunderegister` }
      : undefined,
    ability.can("read", "Location")
      ? { link: "/admin/locations", label: t`Lokasjoner` }
      : undefined,
  ];

  const filteredLinks = links.filter(Boolean).map((link) => ({
    ...link,
    nested: link?.nested?.filter(Boolean),
  })) as {
    link: Routes;
    label: string;
    nested?: {
      link: Routes;
      label: string;
    }[];
  }[];

  const items = filteredLinks.map((link) => {
    const nestedMenuItems = link.nested?.map((item) => (
      <Menu.Item component={Link} to={item.link} key={item.link}>
        {item.label}
      </Menu.Item>
    ));

    if (nestedMenuItems) {
      return (
        <Menu
          key={link.label}
          trigger="click-hover"
          transitionProps={{ exitDuration: 0 }}
          withinPortal
        >
          <Menu.Target>
            <Link key={link.label} to={link.link}>
              {({ isActive }) => (
                <Button
                  size="sm"
                  px="xs"
                  variant={isActive ? "light" : "subtle"}
                >
                  <Center>
                    {link.label}
                    <IconChevronDown size={14} stroke={1.5} />
                  </Center>
                </Button>
              )}
            </Link>
          </Menu.Target>
          <Menu.Dropdown>{nestedMenuItems}</Menu.Dropdown>
        </Menu>
      );
    }

    return (
      <Link key={link.label} to={link.link}>
        {({ isActive }) => (
          <Button size="sm" px="xs" variant={isActive ? "light" : "subtle"}>
            {link.label}
          </Button>
        )}
      </Link>
    );
  });

  return (
    <HeaderBase>
      <Group
        flex={1}
        justify="start"
        gap={5}
        wrap="nowrap"
        style={{ overflowX: "auto" }}
      >
        {items}
      </Group>

      <Can I="create" a={"Booking"}>
        <CreateBooking />
      </Can>

      <HeaderUser />
    </HeaderBase>
  );
}

// ---------- OLD CODE BELOW ----------

// const NavRouterLink = createLink(
//   // eslint-disable-next-line react/display-name
//   React.forwardRef(
//     (
//       props: CreateLinkProps & NavLinkProps,
//       ref: React.ForwardedRef<HTMLAnchorElement>,
//     ) => {
//       return (
//         <NavLink
//           rightSection={<></>}
//           opened={true}
//           {...props}
//           ref={ref}
//           renderRoot={Link}
//         />
//       );
//     },
//   ),
// );

// // eslint-disable-next-line @typescript-eslint/no-unused-vars
// function DesktopNavbar(props: PaperProps) {
//   const { t } = useLingui();
//   return (
//     <Paper
//       withBorder
//       component="nav"
//       w={200}
//       className="flex-shrink-0"
//       radius={0}
//       {...props}
//     >
//       <Stack h={"100%"} gap={0}>
//         <Center p={16} py={32} pt={42}>
//           <img src={logo} alt="Museum Nord" className="h-9" />
//         </Center>

//         <Divider m={0} />

//         <Stack flex={1} pt={16} gap={0}>
//           <NavRouterLink
//             to="/admin/bookings"
//             label={t`Bookinger`}
//             leftSection={<IconReceipt />}
//           />
//           <NavRouterLink
//             to="/admin/products"
//             label={t`Produkt`}
//             leftSection={<IconStar />}
//           >
//             <NavRouterLink
//               to="/admin/products"
//               label={t`Alle produkt`}
//               activeOptions={{ exact: true }}
//               variant="subtle"
//             />
//             <NavRouterLink
//               to="/admin/products/categories"
//               label={t`Kategorier`}
//               variant="subtle"
//             />
//           </NavRouterLink>

//           <NavRouterLink
//             to="/admin/employees"
//             label={t`Personell`}
//             leftSection={<IconUsers />}
//           />

//           <NavRouterLink
//             to="/admin/customers"
//             label={t`Kunderegister`}
//             leftSection={<IconCreditCard />}
//           />

//           <NavRouterLink
//             to="/admin/locations"
//             label={t`Lokasjoner`}
//             leftSection={<IconMapPin />}
//           />
//         </Stack>

//         <Divider />

//         <Flex p={16} align="center" bg="white">
//           <UserButton
//             showName
//             appearance={{
//               elements: {
//                 userButtonBox: {
//                   flexFlow: "row-reverse",
//                 },
//               },
//             }}
//           />
//         </Flex>
//       </Stack>
//     </Paper>
//   );
// }

// // eslint-disable-next-line @typescript-eslint/no-unused-vars
// function MobileNavbar(props: PaperProps) {
//   const { t } = useLingui();
//   return (
//     <Paper
//       withBorder
//       component="nav"
//       w={80}
//       className="flex-shrink-0"
//       {...props}
//     >
//       <Stack h={"100%"} gap={0}>
//         <Center p={16} py={38}>
//           <img src={icon} alt="Museum Nord icon" className="h-10" />
//         </Center>

//         <Divider />

//         <Stack flex={1} gap={4}>
//           <Link to="/">
//             {({ isActive }) => (
//               <MobileNavbarLink
//                 label={t`Bookinger`}
//                 icon={IconReceipt}
//                 active={isActive}
//               />
//             )}
//           </Link>
//           <Link to="/admin/products">
//             {({ isActive }) => (
//               <MobileNavbarLink
//                 label={t`Produkt`}
//                 icon={IconStar}
//                 active={isActive}
//               />
//             )}
//           </Link>
//           <Link to="/admin/employees">
//             {({ isActive }) => (
//               <MobileNavbarLink
//                 label={t`Personell`}
//                 icon={IconUsers}
//                 active={isActive}
//               />
//             )}
//           </Link>
//           <Link to="/admin/products">
//             {({ isActive }) => (
//               <MobileNavbarLink
//                 label={t`Produkt`}
//                 icon={IconStar}
//                 active={isActive}
//               />
//             )}
//           </Link>
//           <Link to="/admin/locations">
//             {({ isActive }) => (
//               <MobileNavbarLink
//                 label={t`Lokasjoner`}
//                 icon={IconMapPin}
//                 active={isActive}
//               />
//             )}
//           </Link>
//         </Stack>

//         <Divider />

//         <Center p={16} bg="white">
//           <UserButton
//             appearance={{
//               elements: {
//                 userButtonAvatarBox: {
//                   width: 32,
//                   height: 32,
//                 },
//               },
//             }}
//           />
//         </Center>
//       </Stack>
//     </Paper>
//   );
// }

// function MobileNavbarLink({
//   icon: Icon,
//   label,
//   active,
// }: {
//   label: string;
//   icon: IconType;
//   active: boolean;
// }) {
//   return (
//     <Center>
//       <Tooltip label={label} position="right">
//         <ActionIcon size={60} variant={active ? "light" : "subtle"}>
//           <Icon size={28} />
//         </ActionIcon>
//       </Tooltip>
//     </Center>
//   );
// }
